import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { usePermissions } from '@/hooks/usePermissions';
import type { Json } from '@/integrations/supabase/types';

// ==================== TYPES ====================

export interface Tenant {
  id: string;
  name: string;
  owner_id: string;
  subscription_plan: string | null;
  subscription_status: string | null;
  subscription_end_date: string | null;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  max_qr_codes: number;
  max_scans: number;
  qr_codes_used: number;
  scans_used: number;
  settings: Json | null;
}

export interface Subscription {
  id: string;
  user_id: string;
  plan_id: string;
  status: string;
  current_period_start: string | null;
  current_period_end: string | null;
  stripe_subscription_id: string | null;
  stripe_customer_id: string | null;
  cancel_at_period_end: boolean;
  plan?: SubscriptionPlan;
}

export interface SubscriptionPlan {
  id: string;
  name: string;
  description: string | null;
  price_monthly: number;
  price_yearly: number | null;
  features: Json | null;
  stripe_price_id_monthly: string | null;
  stripe_price_id_yearly: string | null;
  is_active: boolean;
  qr_limit: number | null;
  menu_limit: number | null;
  booking_enabled: boolean | null;
  site_builder_enabled: boolean | null;
  team_member_limit: number | null;
  api_access: boolean | null;
  white_label: boolean | null;
}

export interface Wallet {
  id: string;
  user_id: string;
  tenant_id: string | null;
  currency: string;
  status: string;
  balance: number;
}

export interface GiftCard {
  id: string;
  code: string;
  amount_minor: number;
  currency: string;
  status: string;
  expires_at: string | null;
  redeemed_at: string | null;
  redeemed_by: string | null;
}

export interface RewardAccount {
  id: string;
  user_id: string;
  current_points: number;
  lifetime_points: number;
  current_tier_id: string | null;
  bookings_count: number;
}

export interface RewardTier {
  id: string;
  name: string;
  min_points: number;
  points_multiplier: number | null;
  benefits: Json | null;
  color: string | null;
  icon: string | null;
}

// ==================== CONTEXT ====================

interface PlatformContextType {
  // Tenant
  tenant: Tenant | null;
  tenantLoading: boolean;
  refreshTenant: () => Promise<void>;
  
  // Subscription
  subscription: Subscription | null;
  subscriptionPlan: SubscriptionPlan | null;
  allPlans: SubscriptionPlan[];
  isSubscribed: boolean;
  subscriptionLoading: boolean;
  checkSubscription: () => Promise<void>;
  createCheckout: (priceId: string) => Promise<string | null>;
  openCustomerPortal: () => Promise<string | null>;
  
  // Wallet
  wallet: Wallet | null;
  walletBalance: number;
  walletLoading: boolean;
  refreshWallet: () => Promise<void>;
  
  // Gift Cards
  giftCards: GiftCard[];
  giftCardsLoading: boolean;
  redeemGiftCard: (code: string) => Promise<boolean>;
  refreshGiftCards: () => Promise<void>;
  
  // Rewards
  rewardAccount: RewardAccount | null;
  currentTier: RewardTier | null;
  allTiers: RewardTier[];
  rewardsLoading: boolean;
  refreshRewards: () => Promise<void>;
  
  // Feature Access
  canAccess: (feature: string) => boolean;
  getFeatureLimit: (feature: string) => number | null;
  
  // Platform-wide loading
  platformLoading: boolean;
}

const PlatformContext = createContext<PlatformContextType | undefined>(undefined);

// ==================== PROVIDER ====================

export const PlatformProvider = ({ children }: { children: ReactNode }) => {
  const { user, session } = useAuth();
  const { hasRole, can } = usePermissions();
  
  // Tenant state
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [tenantLoading, setTenantLoading] = useState(true);
  
  // Subscription state
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [subscriptionPlan, setSubscriptionPlan] = useState<SubscriptionPlan | null>(null);
  const [allPlans, setAllPlans] = useState<SubscriptionPlan[]>([]);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  
  // Wallet state
  const [wallet, setWallet] = useState<Wallet | null>(null);
  const [walletBalance, setWalletBalance] = useState(0);
  const [walletLoading, setWalletLoading] = useState(true);
  
  // Gift cards state
  const [giftCards, setGiftCards] = useState<GiftCard[]>([]);
  const [giftCardsLoading, setGiftCardsLoading] = useState(true);
  
  // Rewards state
  const [rewardAccount, setRewardAccount] = useState<RewardAccount | null>(null);
  const [currentTier, setCurrentTier] = useState<RewardTier | null>(null);
  const [allTiers, setAllTiers] = useState<RewardTier[]>([]);
  const [rewardsLoading, setRewardsLoading] = useState(true);

  // ==================== TENANT FUNCTIONS ====================
  
  const fetchTenant = useCallback(async () => {
    if (!user) {
      setTenant(null);
      setTenantLoading(false);
      return;
    }
    
    try {
      const { data, error } = await supabase
        .from('tenants')
        .select('*')
        .eq('owner_id', user.id)
        .maybeSingle();
      
      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching tenant:', error);
      }
      
      setTenant(data as Tenant | null);
    } catch (err) {
      console.error('Tenant fetch error:', err);
    } finally {
      setTenantLoading(false);
    }
  }, [user]);

  const refreshTenant = useCallback(async () => {
    setTenantLoading(true);
    await fetchTenant();
  }, [fetchTenant]);

  // ==================== SUBSCRIPTION FUNCTIONS ====================
  
  const fetchSubscription = useCallback(async () => {
    if (!user) {
      setSubscription(null);
      setSubscriptionPlan(null);
      setSubscriptionLoading(false);
      return;
    }
    
    try {
      const { data: plansData } = await supabase
        .from('subscription_plans')
        .select('*')
        .eq('is_active', true)
        .order('sort_order');
      
      if (plansData) {
        setAllPlans(plansData as SubscriptionPlan[]);
      }
      
      const { data: subData, error } = await supabase
        .from('user_subscriptions')
        .select('*')
        .eq('user_id', user.id)
        .in('status', ['active', 'trialing'])
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      
      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching subscription:', error);
      }
      
      if (subData) {
        setSubscription(subData as Subscription);
        const plan = (plansData as SubscriptionPlan[] || []).find(p => p.id === subData.plan_id);
        setSubscriptionPlan(plan || null);
      } else {
        setSubscription(null);
        setSubscriptionPlan(null);
      }
    } catch (err) {
      console.error('Subscription fetch error:', err);
    } finally {
      setSubscriptionLoading(false);
    }
  }, [user]);

  const checkSubscription = useCallback(async () => {
    setSubscriptionLoading(true);
    await fetchSubscription();
  }, [fetchSubscription]);

  const createCheckout = useCallback(async (priceId: string): Promise<string | null> => {
    if (!session) return null;
    
    try {
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { priceId },
      });
      
      if (error) throw error;
      return data?.url || null;
    } catch (err) {
      console.error('Checkout error:', err);
      return null;
    }
  }, [session]);

  const openCustomerPortal = useCallback(async (): Promise<string | null> => {
    if (!session) return null;
    
    try {
      const { data, error } = await supabase.functions.invoke('customer-portal', {});
      
      if (error) throw error;
      return data?.url || null;
    } catch (err) {
      console.error('Portal error:', err);
      return null;
    }
  }, [session]);

  // ==================== WALLET FUNCTIONS ====================
  
  const fetchWallet = useCallback(async () => {
    if (!user) {
      setWallet(null);
      setWalletBalance(0);
      setWalletLoading(false);
      return;
    }
    
    try {
      const { data: walletData, error } = await supabase
        .from('wallets')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();
      
      if (error) {
        console.error('Error fetching wallet:', error);
      }
      
      if (walletData) {
        // Get balance from accounts using entries table (double-entry bookkeeping)
        const { data: accounts } = await supabase
          .from('accounts')
          .select('id')
          .eq('wallet_id', walletData.id)
          .eq('kind', 'customer_cash');
        
        let balance = 0;
        if (accounts && accounts.length > 0) {
          const { data: entries } = await supabase
            .from('entries')
            .select('dc, amount_minor')
            .eq('account_id', accounts[0].id);
          
          balance = (entries || []).reduce((sum, e) => {
            // C = Credit (add), D = Debit (subtract)
            if (e.dc === 'C') return sum + Number(e.amount_minor);
            if (e.dc === 'D') return sum - Number(e.amount_minor);
            return sum;
          }, 0) / 100;
        }
        
        setWallet({ ...(walletData as any), balance } as Wallet);
        setWalletBalance(balance);
      } else {
        setWallet(null);
        setWalletBalance(0);
      }
    } catch (err) {
      console.error('Wallet fetch error:', err);
    } finally {
      setWalletLoading(false);
    }
  }, [user]);

  const refreshWallet = useCallback(async () => {
    setWalletLoading(true);
    await fetchWallet();
  }, [fetchWallet]);

  // ==================== GIFT CARD FUNCTIONS ====================
  
  const fetchGiftCards = useCallback(async () => {
    if (!user) {
      setGiftCards([]);
      setGiftCardsLoading(false);
      return;
    }
    
    try {
      const { data, error } = await supabase
        .from('gift_cards')
        .select('*')
        .eq('redeemed_by', user.id)
        .order('created_at', { ascending: false });
      
      if (error) {
        console.error('Error fetching gift cards:', error);
      }
      
      setGiftCards((data as GiftCard[]) || []);
    } catch (err) {
      console.error('Gift cards fetch error:', err);
    } finally {
      setGiftCardsLoading(false);
    }
  }, [user]);

  const redeemGiftCard = useCallback(async (code: string): Promise<boolean> => {
    if (!user) return false;
    
    try {
      const { data: giftCard, error: findError } = await supabase
        .from('gift_cards')
        .select('*')
        .eq('code', code.toUpperCase())
        .eq('status', 'active')
        .single();
      
      if (findError || !giftCard) {
        console.error('Gift card not found or invalid');
        return false;
      }
      
      if (giftCard.expires_at && new Date(giftCard.expires_at) < new Date()) {
        console.error('Gift card expired');
        return false;
      }
      
      const { error: updateError } = await supabase
        .from('gift_cards')
        .update({
          status: 'redeemed',
          redeemed_by: user.id,
          redeemed_at: new Date().toISOString(),
        })
        .eq('id', giftCard.id);
      
      if (updateError) throw updateError;
      
      await refreshWallet();
      await refreshGiftCards();
      
      return true;
    } catch (err) {
      console.error('Redeem error:', err);
      return false;
    }
  }, [user, refreshWallet]);

  const refreshGiftCards = useCallback(async () => {
    setGiftCardsLoading(true);
    await fetchGiftCards();
  }, [fetchGiftCards]);

  // ==================== REWARDS FUNCTIONS ====================
  
  const fetchRewards = useCallback(async () => {
    if (!user) {
      setRewardAccount(null);
      setCurrentTier(null);
      setAllTiers([]);
      setRewardsLoading(false);
      return;
    }
    
    try {
      const { data: tiersData } = await supabase
        .from('resv_reward_tiers')
        .select('*')
        .order('min_points', { ascending: true });
      
      const mappedTiers: RewardTier[] = (tiersData || []).map(t => ({
        id: t.id,
        name: t.name,
        min_points: t.min_points,
        points_multiplier: t.points_multiplier,
        benefits: t.benefits,
        color: t.color,
        icon: t.icon,
      }));
      
      setAllTiers(mappedTiers);
      
      const { data: accountData, error } = await supabase
        .from('resv_reward_accounts')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();
      
      if (error) {
        console.error('Error fetching reward account:', error);
      }
      
      if (accountData) {
        const mappedAccount: RewardAccount = {
          id: accountData.id,
          user_id: accountData.user_id,
          current_points: accountData.current_points,
          lifetime_points: accountData.lifetime_points,
          current_tier_id: accountData.current_tier_id,
          bookings_count: accountData.bookings_count,
        };
        
        setRewardAccount(mappedAccount);
        
        const sortedTiers = [...mappedTiers].sort((a, b) => b.min_points - a.min_points);
        const tier = sortedTiers.find(t => mappedAccount.lifetime_points >= t.min_points);
        setCurrentTier(tier || null);
      } else {
        setRewardAccount(null);
        setCurrentTier(null);
      }
    } catch (err) {
      console.error('Rewards fetch error:', err);
    } finally {
      setRewardsLoading(false);
    }
  }, [user]);

  const refreshRewards = useCallback(async () => {
    setRewardsLoading(true);
    await fetchRewards();
  }, [fetchRewards]);

  // ==================== FEATURE ACCESS ====================
  
  const canAccess = useCallback((feature: string): boolean => {
    if (hasRole(['admin', 'owner'])) return true;
    if (can('access', feature)) return true;
    
    if (subscriptionPlan) {
      const features = subscriptionPlan.features as Record<string, unknown> | null;
      if (features && features[feature] === true) return true;
      
      if (feature === 'booking' && subscriptionPlan.booking_enabled) return true;
      if (feature === 'site_builder' && subscriptionPlan.site_builder_enabled) return true;
      if (feature === 'api' && subscriptionPlan.api_access) return true;
      if (feature === 'white_label' && subscriptionPlan.white_label) return true;
    }
    
    return false;
  }, [hasRole, can, subscriptionPlan]);

  const getFeatureLimit = useCallback((feature: string): number | null => {
    if (!subscriptionPlan) return null;
    
    switch (feature) {
      case 'qr_codes': return subscriptionPlan.qr_limit;
      case 'menus': return subscriptionPlan.menu_limit;
      case 'team_members': return subscriptionPlan.team_member_limit;
      default: return null;
    }
  }, [subscriptionPlan]);

  // ==================== EFFECTS ====================
  
  useEffect(() => {
    if (user) {
      Promise.all([
        fetchTenant(),
        fetchSubscription(),
        fetchWallet(),
        fetchGiftCards(),
        fetchRewards(),
      ]);
    } else {
      setTenant(null);
      setSubscription(null);
      setSubscriptionPlan(null);
      setWallet(null);
      setWalletBalance(0);
      setGiftCards([]);
      setRewardAccount(null);
      setCurrentTier(null);
      setTenantLoading(false);
      setSubscriptionLoading(false);
      setWalletLoading(false);
      setGiftCardsLoading(false);
      setRewardsLoading(false);
    }
  }, [user]);

  // ==================== COMPUTED ====================
  
  const isSubscribed = !!subscription && subscription.status === 'active';
  const platformLoading = tenantLoading || subscriptionLoading || walletLoading || rewardsLoading;

  // ==================== CONTEXT VALUE ====================
  
  const value: PlatformContextType = {
    tenant, tenantLoading, refreshTenant,
    subscription, subscriptionPlan, allPlans, isSubscribed, subscriptionLoading,
    checkSubscription, createCheckout, openCustomerPortal,
    wallet, walletBalance, walletLoading, refreshWallet,
    giftCards, giftCardsLoading, redeemGiftCard, refreshGiftCards,
    rewardAccount, currentTier, allTiers, rewardsLoading, refreshRewards,
    canAccess, getFeatureLimit, platformLoading,
  };

  return (
    <PlatformContext.Provider value={value}>
      {children}
    </PlatformContext.Provider>
  );
};

// ==================== HOOKS ====================

export const usePlatform = () => {
  const context = useContext(PlatformContext);
  if (context === undefined) {
    throw new Error('usePlatform must be used within a PlatformProvider');
  }
  return context;
};

export const useSubscription = () => {
  const { subscription, subscriptionPlan, allPlans, isSubscribed, subscriptionLoading, 
          checkSubscription, createCheckout, openCustomerPortal } = usePlatform();
  return {
    subscription, plan: subscriptionPlan, allPlans, isSubscribed,
    loading: subscriptionLoading, refresh: checkSubscription,
    createCheckout, openCustomerPortal,
  };
};

export const useWallet = () => {
  const { wallet, walletBalance, walletLoading, refreshWallet,
          giftCards, giftCardsLoading, redeemGiftCard, refreshGiftCards } = usePlatform();
  return {
    wallet, balance: walletBalance, loading: walletLoading, refresh: refreshWallet,
    giftCards, giftCardsLoading, redeemGiftCard, refreshGiftCards,
  };
};

export const usePlatformRewards = () => {
  const { rewardAccount, currentTier, allTiers, rewardsLoading, refreshRewards } = usePlatform();
  return {
    account: rewardAccount,
    points: rewardAccount?.current_points || 0,
    totalPoints: rewardAccount?.lifetime_points || 0,
    tier: currentTier, allTiers,
    loading: rewardsLoading, refresh: refreshRewards,
  };
};

export const useTenant = () => {
  const { tenant, tenantLoading, refreshTenant } = usePlatform();
  return { tenant, loading: tenantLoading, refresh: refreshTenant };
};

export const useFeatureAccess = () => {
  const { canAccess, getFeatureLimit, isSubscribed, subscriptionPlan } = usePlatform();
  return { canAccess, getFeatureLimit, isSubscribed, plan: subscriptionPlan };
};
